import { supabase } from "./supabaseClient";

// ============================================================
// Visual Library data layer.
//
// Records now live in the org-scoped `visual_library` Supabase
// table (see supabase/migrations/20260624_visual_library.sql), so
// photos sync across Jay, Lou, and mobile. Image bytes live in the
// public `visual-library` Storage bucket; this module maps a product
// to its uploaded photo and handles list/upsert/delete.
//
// The pure helpers (buildVisualProductKey, VISUAL_LIBRARY_PRIORITY_CATEGORIES)
// remain here; proposal matching now resolves visuals in App.buildSnapshot and
// freezes them into the snapshot as ProposalVisual[] (URLs only).
// ============================================================

export const STORAGE_BUCKET = "visual-library";

export type VisualLibraryRecord = {
  id: string;
  category: string;
  productKey: string;
  displayName: string;
  imageRef: string; // public URL (image_url) — what the proposal renderer displays
  caption?: string;
  createdAt: string;
  updatedAt: string;
  // DB-backed fields. Optional during the migration so the old admin page
  // (which constructs records without them) still compiles; the new admin
  // page and the DB layer always populate them.
  orgId?: string;
  productId?: string | null; // pricing_items2 id; null for Skirting/Lattice
  imagePath?: string | null; // object path within the bucket, for deletion
};

// Lightweight visual embedded into a proposal snapshot (URLs only, no bytes),
// so unauthenticated /review customers can render the appendix without reading
// the org-scoped visual_library table.
export type ProposalVisual = {
  category: string;
  label: string;
  image_url: string;
  caption?: string;
};

export const VISUAL_LIBRARY_PRIORITY_CATEGORIES = [
  "Decking",
  "Railing",
  "Skirting",
  "Lattice",
  "Fasteners",
  "Stairs",
  "Demo",
  "Bench",
  "Gate",
] as const;

function normalize(value: string): string {
  return (value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
}

export function buildVisualProductKey(category: string, name: string): string {
  const cat = (category || "").toLowerCase();
  const canonical = cat.includes("skirt") || cat.includes("latt") ? "Skirting / Lattice" : category;
  return `${normalize(canonical)}:${normalize(name)}`;
}

// ------------------------------------------------------------
// Resolve the matched proposal visuals for the current selections. Shared by
// the desktop estimator (App.tsx buildSnapshot) and the mobile proposal screen
// so both freeze the SAME appendix into a proposal. Given the org's visual
// library records + the selected product rows, returns the ProposalVisual[]
// (URLs only) the proposal renderer displays.
// ------------------------------------------------------------
export function resolveProposalVisualsFrom(
  records: VisualLibraryRecord[],
  sel: {
    decking?: { id?: unknown; name?: string } | null;
    railing?: { id?: unknown; name?: string } | null;
    skirting?: { id?: unknown; name?: string } | null;
    fastener?: { id?: unknown; name?: string } | null;
    skirtingCategory?: string;
    addItems?: any[] | null;
    // Whether stairs are actually part of the job (main Stairs configured OR a
    // "Stair options" add-item present). Gates the Stairs appendix photo so a
    // deck-only job never shows a staircase.
    stairsPresent?: boolean;
    // Whether demo is part of the job (a demo line item present / qty > 0).
    demoPresent?: boolean;
    // Whether a Gate add-item is present on the estimate. Gates the Gate
    // appendix photo, which is keyed to the SELECTED RAILING (one gate photo
    // per railing product). No gate add-item → no gate photo, even if one exists.
    gatePresent?: boolean;
  }
): ProposalVisual[] {
  const out: ProposalVisual[] = [];
  const byId = (id: unknown) =>
    records.find((v) => v.productId && v.productId === String(id) && v.imageRef);
  // Id-less fixed-slot lookup (Skirting / Stairs / Demo / Bench) — keyed by the
  // canonical product_key, matching what the admin page uploads.
  const byKey = (category: string, value: string) => {
    const wantedKey = buildVisualProductKey(category, value);
    return records.find(
      (v) => !v.productId && v.productKey === wantedKey && v.imageRef
    );
  };

  if (sel.decking?.id) {
    const r = byId(sel.decking.id);
    if (r) out.push({ category: "Decking", label: sel.decking.name || "", image_url: r.imageRef, caption: r.caption || "" });
  }
  if (sel.railing?.id) {
    const r = byId(sel.railing.id);
    if (r) out.push({ category: "Railing", label: sel.railing.name || "", image_url: r.imageRef, caption: r.caption || "" });
  }
  if (sel.fastener?.id) {
    const r = byId(sel.fastener.id);
    if (r) out.push({ category: "Fasteners", label: sel.fastener.name || "", image_url: r.imageRef, caption: r.caption || "" });
  }
  if (sel.skirting?.id) {
    const isLattice = (sel.skirtingCategory || "").toLowerCase().includes("latt");
    if (isLattice) {
      const r = byId(sel.skirting.id);
      if (r) out.push({ category: "Lattice", label: sel.skirting.name || "", image_url: r.imageRef, caption: r.caption || "" });
    } else {
      // Skirting has two fixed, id-less cards: Vertical and Horizontal. Detect
      // horizontal exactly the way ContractPage does — the selected skirting
      // name carries the "horiz" stem (note the DB's "Horiztonal" misspelling),
      // OR an +Add Item's name has "horiz" / its description has "horizontal".
      const mainIsHorizontal = (sel.skirting.name || "").toLowerCase().includes("horiz");
      const hasHorizontalAddItem = (sel.addItems || []).some((row: any) => {
        const n = (row?.picked?.name || "").toLowerCase();
        const d = (row?.picked?.proposal_description || "").toLowerCase();
        return n.includes("horiz") || d.includes("horizontal");
      });
      const slot = mainIsHorizontal || hasHorizontalAddItem ? "Horizontal Skirting" : "Vertical Skirting";
      const wantedKey = buildVisualProductKey("Skirting", slot);
      const r = records.find((v) => !v.productId && v.productKey === wantedKey && v.imageRef);
      if (r) out.push({ category: "Skirting", label: sel.skirting.name || "", image_url: r.imageRef, caption: r.caption || "" });
    }
  }

  // STAIRS — only when stairs are in the job. Mitered add-item (scan add-items
  // only, by name) → Mitered Treads photo; otherwise → Standard Staircase.
  if (sel.stairsPresent) {
    const hasMitered = (sel.addItems || []).some((row: any) => {
      const n = (row?.picked?.name || row?.displayName || "").toLowerCase();
      return n.includes("mitered");
    });
    const value = hasMitered ? "Mitered Treads" : "Standard Staircase";
    const r = byKey("Stairs", value);
    if (r) out.push({ category: "Stairs", label: value, image_url: r.imageRef, caption: r.caption || "" });
  }

  // DEMO — single photo whenever demo is part of the job, regardless of type.
  if (sel.demoPresent) {
    const r = byKey("Demo", "Demo");
    if (r) out.push({ category: "Demo", label: "Demo", image_url: r.imageRef, caption: r.caption || "" });
  }

  // BENCH — one photo per selected bench type. Only "12_flat" / "18_flat" have
  // slots today; other bench types (w/back, w/storage) show nothing. Deduped so
  // multiple benches of the same type render at most one card.
  const BENCH_SLOTS: Record<string, string> = {
    "12_flat": "12 Flat",
    "18_flat": "18 Flat",
  };
  const seenBench = new Set<string>();
  for (const row of (sel.addItems || []) as any[]) {
    const cat = (row?.category || "")
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, "_");
    if (cat !== "bench") continue;
    const bt = String(row?.benchType || "");
    const slot = BENCH_SLOTS[bt];
    if (!slot || seenBench.has(bt)) continue;
    seenBench.add(bt);
    const r = byKey("Bench", slot);
    if (r) out.push({ category: "Bench", label: slot, image_url: r.imageRef, caption: r.caption || "" });
  }

  // GATE — only when a Gate add-item is on the estimate. The gate photo is
  // keyed to the SELECTED RAILING via a namespaced product_id ("gate:<railingId>")
  // so it never collides with the Railing category's own photo for the same
  // railing. If no gate photo exists for that railing, show nothing.
  if (sel.gatePresent && sel.railing?.id) {
    const gateProductId = `gate:${String(sel.railing.id)}`;
    const r = records.find(
      (v) => v.productId === gateProductId && v.imageRef
    );
    if (r) out.push({ category: "Gate", label: sel.railing.name || "", image_url: r.imageRef, caption: r.caption || "" });
  }

  return out;
}

// ------------------------------------------------------------
// DB row <-> app record mapping
// ------------------------------------------------------------
type VisualLibraryRow = {
  id: string;
  org_id: string;
  category: string;
  product_id: string | null;
  product_key: string;
  display_name: string;
  image_path: string | null;
  image_url: string | null;
  caption: string | null;
  created_at: string;
  updated_at: string;
};

function rowToRecord(row: VisualLibraryRow): VisualLibraryRecord {
  return {
    id: row.id,
    orgId: row.org_id,
    category: row.category,
    productId: row.product_id,
    productKey: row.product_key,
    displayName: row.display_name,
    imagePath: row.image_path,
    imageRef: row.image_url || "",
    caption: row.caption || "",
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export type VisualUpsertInput = {
  orgId: string;
  category: string;
  productId: string | null; // pricing_items2 id (Decking/Railing); null for Skirting/Lattice
  productKey: string;
  displayName: string;
  imagePath?: string | null;
  imageRef?: string; // public URL
  caption?: string;
};

// ------------------------------------------------------------
// Read: all visuals for an org (RLS also enforces this server-side).
// ------------------------------------------------------------
export async function listVisualsForOrg(orgId: string): Promise<VisualLibraryRecord[]> {
  if (!orgId) return [];
  const { data, error } = await supabase
    .from("visual_library")
    .select("*")
    .eq("org_id", orgId)
    .order("category", { ascending: true })
    .order("display_name", { ascending: true });

  if (error) throw error;
  return (data || []).map((row) => rowToRecord(row as VisualLibraryRow));
}

// ------------------------------------------------------------
// Upsert: one photo per product. Match identity on the stable
// pricing_items2 id when present (Decking/Railing) so renames don't
// orphan a photo; fall back to the canonical style key for
// Skirting/Lattice (which have no single product id).
//
// Done as read-modify-write because the uniqueness index is on the
// expression coalesce(product_id, product_key), which supabase-js
// onConflict (column names only) cannot target directly.
// ------------------------------------------------------------
export async function upsertVisual(input: VisualUpsertInput): Promise<VisualLibraryRecord> {
  const matchColumn = input.productId ? "product_id" : "product_key";
  const matchValue = input.productId ? input.productId : input.productKey;

  const { data: existing, error: findErr } = await supabase
    .from("visual_library")
    .select("id")
    .eq("org_id", input.orgId)
    .eq(matchColumn, matchValue)
    .maybeSingle();
  if (findErr) throw findErr;

  const payload = {
    org_id: input.orgId,
    category: input.category,
    product_id: input.productId,
    product_key: input.productKey,
    display_name: input.displayName,
    image_path: input.imagePath ?? null,
    image_url: input.imageRef ?? null,
    caption: input.caption ?? null,
  };

  if (existing?.id) {
    const { data, error } = await supabase
      .from("visual_library")
      .update(payload)
      .eq("id", existing.id)
      .select("*")
      .single();
    if (error) throw error;
    return rowToRecord(data as VisualLibraryRow);
  }

  const { data, error } = await supabase
    .from("visual_library")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw error;
  return rowToRecord(data as VisualLibraryRow);
}

// ------------------------------------------------------------
// Delete: remove BOTH the bucket object and the DB row, returning
// the card to its empty state. Storage removal is best-effort (a
// missing object should not block deleting the row).
// ------------------------------------------------------------
export async function deleteVisual(record: { id: string; imagePath?: string | null }): Promise<void> {
  if (record.imagePath) {
    const { error: storageErr } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([record.imagePath]);
    if (storageErr) {
      // Don't abort the row delete on a storage miss — log and continue.
      console.warn("visual-library: storage object remove failed:", storageErr.message);
    }
  }

  const { error } = await supabase.from("visual_library").delete().eq("id", record.id);
  if (error) throw error;
}

